import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../config/axios';
import { Meta, Display, Icons } from '../components/Atoms';

/**
 * Photo Capture - aparat z 3 trybami w stylu Terroir.
 * Z paczki photo.jsx (ScreenCapture) zaadaptowane.
 *
 * Tryby:
 *   1. Zdjęcie - zwykłe capture frame z kamery
 *   2. Rozpoznaj - capture + AI rozpoznawanie rośliny (TBD - wymaga LLM)
 *   3. Skanuj choroby - capture + ML model dla diagnoz chorób (TBD - wymaga modelu)
 *
 * V1 (ten sandbox):
 *   - Real Camera API (MediaDevices.getUserMedia) - live preview
 *   - Capture frame → canvas → blob → POST /api/gallery/quick (zaimplementowane)
 *   - Tryby Rozpoznaj/Skanuj choroby - placeholder UI z dema AI box
 *
 * Permission handling:
 *   - Pyta o dostęp do kamery przy wejściu
 *   - Fallback: pokaż dema viewfinder + komunikat "Zezwól na kamerę"
 */

const MODES = [
  { k: 'photo', label: 'Zdjęcie' },
  { k: 'qr', label: 'QR' },
];

export default function Capture() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [mode, setMode] = useState('photo');
  const [stream, setStream] = useState(null);
  const [permissionState, setPermissionState] = useState('pending'); // pending | granted | denied
  const [lastShot, setLastShot] = useState(null);
  const [facingMode, setFacingMode] = useState('environment'); // 'environment' = tylna, 'user' = przednia
  const [uploadStatus, setUploadStatus] = useState(null); // null | 'uploading' | 'saved' | 'error'
  const [uploadMsg, setUploadMsg] = useState('');
  const [qrResult, setQrResult] = useState(null); // odczytany kod QR
  const [qrStatus, setQrStatus] = useState('idle'); // idle | scanning | found | notfound | error
  const [plantMatch, setPlantMatch] = useState(null); // dopasowana roślina

  // Inicjalizacja kamery
  useEffect(() => {
    let activeStream = null;
    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setPermissionState('denied');
          return;
        }
        activeStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode },
          audio: false,
        });
        setStream(activeStream);
        setPermissionState('granted');
        if (videoRef.current) {
          videoRef.current.srcObject = activeStream;
          await videoRef.current.play().catch(() => {});
        }
      } catch (err) {
        setPermissionState('denied');
      }
    };
    start();
    return () => {
      if (activeStream) activeStream.getTracks().forEach(t => t.stop());
    };
  }, [facingMode]);

  // Capture frame + upload do galerii
  const handleShutter = async () => {
    if (!videoRef.current || !canvasRef.current) return;
    if (uploadStatus === 'uploading') return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Pokaż w preview thumb od razu (optimistic)
    setLastShot(canvas.toDataURL('image/jpeg', 0.85));

    // Upload do galerii
    setUploadStatus('uploading');
    setUploadMsg('');
    try {
      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(b => b ? resolve(b) : reject(new Error('Nie udało się utworzyć pliku')), 'image/jpeg', 0.88);
      });
      const formData = new FormData();
      const ts = new Date().toISOString().replace(/[:.]/g, '-');
      formData.append('photo', blob, `capture-${ts}.jpg`);
      // tag dopasowany do trybu — backend whitelistuje wartości w ALLOWED_TAGS
      const tagByMode = { photo: 'ogólne', identify: 'ogólne', disease: 'problem' };
      const tag = tagByMode[mode] || 'ogólne';
      formData.append('tag', tag);

      await axios.post('/api/gallery/quick', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadStatus('saved');
      setUploadMsg('Zapisano w galerii');
      setTimeout(() => { setUploadStatus(null); setUploadMsg(''); }, 2500);
    } catch (err) {
      setUploadStatus('error');
      setUploadMsg(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Nie zapisano — spróbuj ponownie');
      setTimeout(() => { setUploadStatus(null); setUploadMsg(''); }, 4000);
    }
  };

  const flip = () => setFacingMode(facingMode === 'environment' ? 'user' : 'environment');

  // QR scanner — uruchamiany gdy tryb = 'qr'
  useEffect(() => {
    if (mode !== 'qr') { setQrStatus('idle'); setQrResult(null); setPlantMatch(null); return; }

    let scanner = null;
    let cancelled = false;

    const startScan = async () => {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        scanner = new Html5Qrcode('qr-reader');
        setQrStatus('scanning');

        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (decodedText) => {
            if (cancelled) return;
            scanner.stop().catch(() => {});
            setQrResult(decodedText);
            setQrStatus('found');

            // Spróbuj dopasować do bazy roślin
            try {
              const res = await axios.get(`/api/plants?limit=20&search=${encodeURIComponent(decodedText)}`);
              const plants = res.data?.data || [];
              // Szukaj po nazwie lub display_name
              const match = plants.find(p =>
                p.name?.toLowerCase() === decodedText.toLowerCase() ||
                p.display_name?.toLowerCase() === decodedText.toLowerCase() ||
                p.latin_name?.toLowerCase() === decodedText.toLowerCase()
              );
              if (match) {
                setPlantMatch(match);
              } else {
                setPlantMatch(null);
              }
            } catch {
              setPlantMatch(null);
            }
          },
          () => {} // on failure — ciche pomijanie
        );
      } catch (err) {
        if (!cancelled) {
          setQrStatus('error');
        }
      }
    };

    if (mode === 'qr') startScan();

    return () => {
      cancelled = true;
      if (scanner) scanner.stop().catch(() => {});
    };
  }, [mode]);

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: '#000',
      zIndex: 1000, overflow: 'hidden',
    }}>
      {/* Live viewfinder lub fallback */}
      {permissionState === 'granted' ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
          }}
        />
      ) : (
        <DemaViewfinder permissionState={permissionState} />
      )}

      {/* Hidden canvas dla capture frames */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {/* QR reader container (tryb QR) */}
      {mode === 'qr' && permissionState === 'granted' && (
        <div id="qr-reader" style={{
          position: 'absolute', inset: 0,
          width: '100%', height: '100%',
        }} />
      )}

      {/* AI detection box + label (tylko w trybie Rozpoznaj/Choroby) */}
      {(mode === 'identify' || mode === 'disease') && permissionState === 'granted' && (
        <DetectionFrame mode={mode} />
      )}

      {/* QR overlay (tryb QR) */}
      {mode === 'qr' && (
        <div style={{
          position: 'absolute', bottom: '30%', left: 0, right: 0, zIndex: 15,
          textAlign: 'center',
          pointerEvents: 'none',
        }}>
          <div style={{
            display: 'inline-block',
            padding: '8px 16px', borderRadius: 8,
            background: 'rgba(0,0,0,0.5)',
            backdropFilter: 'blur(8px)',
            color: '#fff',
            fontFamily: 'var(--t-mono)', fontSize: 11,
            letterSpacing: '.14em',
          }}>
            {qrStatus === 'scanning' ? '🔍 Skieruj na kod QR' :
             qrStatus === 'found' ? '✓ Odczytano' :
             qrStatus === 'error' ? '✗ Błąd kamery' : ''}
          </div>
        </div>
      )}

      {/* QR result card */}
      {mode === 'qr' && qrResult && (
        <div style={{
          position: 'absolute', bottom: 120, left: 20, right: 20, zIndex: 25,
          padding: '14px 16px', borderRadius: 12,
          background: 'rgba(15, 20, 16, 0.85)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.1)',
          color: '#fff',
        }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>
            Odczytany kod:
          </div>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10, wordBreak: 'break-all' }}>
            {qrResult}
          </div>
          {plantMatch ? (
            <div style={{
              padding: '8px 12px', borderRadius: 8,
              background: 'rgba(168, 196, 158, 0.15)',
              border: '1px solid rgba(168, 196, 158, 0.3)',
              fontSize: 13, color: 'var(--t-sage)',
            }}>
              ✓ Dopasowano: {plantMatch.display_name || plantMatch.name}
            </div>
          ) : (
            <div style={{
              fontSize: 12, color: 'rgba(255,255,255,0.5)',
              fontStyle: 'italic',
            }}>
              Nie znaleziono w bazie — możesz wpisać ręcznie
            </div>
          )}
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => navigate(`/plants?search=${encodeURIComponent(qrResult)}`)}
              style={{
                flex: 1, padding: '8px 12px', borderRadius: 8,
                background: 'var(--t-sage)', border: 'none',
                color: 'var(--t-bg)', fontWeight: 600,
                fontFamily: 'var(--t-sans)', fontSize: 13,
                cursor: 'pointer',
              }}
            >Szukaj w katalogu</button>
            <button
              type="button"
              onClick={() => { setQrStatus('scanning'); setQrResult(null); setPlantMatch(null); }}
              style={{
                padding: '8px 12px', borderRadius: 8,
                background: 'transparent', border: '1px solid rgba(255,255,255,0.2)',
                color: '#fff', fontFamily: 'var(--t-sans)', fontSize: 13,
                cursor: 'pointer',
              }}
            >Skanuj ponownie</button>
          </div>
        </div>
      )}

      {/* Grid overlay (rule of thirds) - opcjonalny */}
      {permissionState === 'granted' && (
        <svg style={{ position: 'absolute', inset: 0, opacity: 0.12, pointerEvents: 'none' }}
             width="100%" height="100%" aria-hidden="true">
          <line x1="33.33%" y1="0" x2="33.33%" y2="100%" stroke="white" strokeWidth="1" />
          <line x1="66.66%" y1="0" x2="66.66%" y2="100%" stroke="white" strokeWidth="1" />
          <line x1="0" y1="33.33%" x2="100%" y2="33.33%" stroke="white" strokeWidth="1" />
          <line x1="0" y1="66.66%" x2="100%" y2="66.66%" stroke="white" strokeWidth="1" />
        </svg>
      )}

      {/* Top bar */}
      <div style={{
        position: 'absolute',
        top: 'max(env(safe-area-inset-top, 0), 20px)',
        left: 0, right: 0, zIndex: 20,
        padding: '0 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <button
          onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/gallery')}
          aria-label={t('common.close')}
          style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.45)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: 'none', color: 'var(--t-text)',
            fontSize: 18, cursor: 'pointer',
            display: 'grid', placeItems: 'center',
          }}
        >×</button>

        <ModeIndicator mode={mode} />

        <button
          aria-label="Ustawienia"
          style={{
            width: 40, height: 40, borderRadius: '50%',
            background: 'rgba(0, 0, 0, 0.45)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: 'none', cursor: 'pointer',
            display: 'grid', placeItems: 'center',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M9 2v3M9 13v3M2 9h3M13 9h3M4 4l2 2M12 12l2 2M14 4l-2 2M4 14l2-2"
                  stroke="var(--t-text)" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="9" cy="9" r="2.5" stroke="var(--t-text)" strokeWidth="1.5" />
          </svg>
        </button>
      </div>

      {/* Bottom controls */}
      <div style={{
        position: 'absolute',
        bottom: 'max(env(safe-area-inset-bottom, 0), 20px)',
        left: 0, right: 0, zIndex: 20,
        padding: '0 0 14px',
      }}>
        {/* AI confirmation card (tylko gdy Rozpoznaj + granted) */}
        {mode === 'identify' && permissionState === 'granted' && (
          <DetectionCard />
        )}

        {/* Mode pills */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 22,
          marginBottom: 22,
          fontFamily: 'var(--t-mono)', fontSize: 10,
          letterSpacing: '.22em', textTransform: 'uppercase',
        }}>
          {MODES.map(m => {
            const on = mode === m.k;
            return (
              <button
                key={m.k}
                onClick={() => setMode(m.k)}
                style={{
                  background: 'transparent', border: 'none',
                  color: on ? 'var(--t-sage)' : 'rgba(255, 255, 255, 0.45)',
                  fontFamily: 'inherit', fontSize: 'inherit',
                  letterSpacing: 'inherit', textTransform: 'inherit',
                  fontWeight: on ? 600 : 400,
                  cursor: 'pointer', position: 'relative',
                  padding: '4px 0',
                }}
              >
                {m.label}
                {on && (
                  <div style={{
                    position: 'absolute', bottom: -4, left: 0, right: 0,
                    height: 2, background: 'var(--t-sage)', borderRadius: 1,
                  }} />
                )}
              </button>
            );
          })}
        </div>

        {/* Status uploadu — toast nad shutter row */}
        {uploadStatus && (
          <div style={{
            margin: '0 auto 14px',
            padding: '10px 16px', borderRadius: 999,
            background: uploadStatus === 'saved'
              ? 'rgba(168, 196, 158, 0.2)'
              : uploadStatus === 'error'
              ? 'rgba(192, 106, 74, 0.2)'
              : 'rgba(15, 20, 16, 0.7)',
            border: `1px solid ${
              uploadStatus === 'saved' ? 'var(--t-sage)' :
              uploadStatus === 'error' ? 'var(--t-rust)' :
              'var(--t-line-2)'
            }`,
            color: uploadStatus === 'saved' ? 'var(--t-sage)' :
                   uploadStatus === 'error' ? 'var(--t-rust)' :
                   'var(--t-text)',
            fontFamily: 'var(--t-mono)', fontSize: 11,
            letterSpacing: '.14em', textTransform: 'uppercase',
            display: 'inline-block',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            alignSelf: 'center',
          }}>
            {uploadStatus === 'uploading' ? 'Zapisuję…' : uploadMsg}
          </div>
        )}

        {/* Shutter row */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 40px',
        }}>
          {/* Last photo thumbnail */}
          <button
            aria-label="Ostatnie zdjęcie"
            style={{
              width: 46, height: 46, borderRadius: 10,
              background: lastShot
                ? `url(${lastShot}) center / cover`
                : 'linear-gradient(135deg, var(--t-amber) 50%, var(--t-rust))',
              border: '2px solid var(--t-text)',
              cursor: 'pointer',
            }}
            onClick={() => navigate('/gallery')}
          />

          {/* Shutter */}
          <button
            onClick={handleShutter}
            disabled={permissionState !== 'granted'}
            aria-label="Zrób zdjęcie"
            style={{
              width: 78, height: 78, borderRadius: '50%',
              border: '3px solid var(--t-text)',
              padding: 5, background: 'transparent',
              cursor: permissionState === 'granted' ? 'pointer' : 'not-allowed',
              opacity: permissionState === 'granted' ? 1 : 0.4,
            }}
            onMouseDown={e => { if (permissionState === 'granted') e.currentTarget.style.transform = 'scale(0.92)'; }}
            onMouseUp={e => { e.currentTarget.style.transform = 'scale(1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            <div style={{
              width: '100%', height: '100%', borderRadius: '50%',
              background: 'var(--t-text)',
              transition: 'background 200ms ease',
            }} />
          </button>

          {/* Flip camera */}
          <button
            onClick={flip}
            aria-label="Przełącz kamerę"
            style={{
              width: 46, height: 46, borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.12)',
              border: 'none', cursor: 'pointer',
              display: 'grid', placeItems: 'center',
            }}
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path d="M3 11a8 8 0 0114-5M19 11a8 8 0 01-14 5"
                    stroke="var(--t-text)" strokeWidth="1.6" strokeLinecap="round" />
              <path d="M15 2l4 4-4 4M7 20l-4-4 4-4"
                    stroke="var(--t-text)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Mode indicator (top center pill) ─────────────────────────────
function ModeIndicator({ mode }) {
  const m = MODES.find(x => x.k === mode);
  return (
    <div style={{
      padding: '8px 14px', borderRadius: 999,
      background: 'rgba(0, 0, 0, 0.45)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      fontFamily: 'var(--t-mono)', fontSize: 10,
      letterSpacing: '.18em', textTransform: 'uppercase',
      color: 'var(--t-text)', fontWeight: 500,
      display: 'flex', alignItems: 'center', gap: 8,
    }}>
      <div style={{
        width: 6, height: 6, borderRadius: '50%',
        background: 'var(--t-sage)',
        boxShadow: '0 0 6px var(--t-sage)',
      }} />
      {m?.label}
    </div>
  );
}

// ── Detection frame (centralna ramka AI) ─────────────────────────
function DetectionFrame({ mode }) {
  const color = mode === 'disease' ? 'var(--t-rust)' : 'var(--t-sage)';
  const label = mode === 'disease' ? 'Skanowanie...' : 'Pomidor · 94%';
  return (
    <div style={{
      position: 'absolute', top: '28%', left: '22%', right: '22%', bottom: '38%',
      border: `1.5px solid ${color}`,
      borderRadius: 12, pointerEvents: 'none',
    }}>
      {/* Corners */}
      {[['t', 'l'], ['t', 'r'], ['b', 'l'], ['b', 'r']].map(([v, h], i) => (
        <div key={i} style={{
          position: 'absolute',
          [v === 't' ? 'top' : 'bottom']: -2,
          [h === 'l' ? 'left' : 'right']: -2,
          width: 18, height: 18,
          borderTop: v === 't' ? `3px solid ${color}` : 'none',
          borderBottom: v === 'b' ? `3px solid ${color}` : 'none',
          borderLeft: h === 'l' ? `3px solid ${color}` : 'none',
          borderRight: h === 'r' ? `3px solid ${color}` : 'none',
          borderRadius:
            h === 'l' && v === 't' ? '4px 0 0 0' :
            h === 'r' && v === 't' ? '0 4px 0 0' :
            h === 'l' && v === 'b' ? '0 0 0 4px' : '0 0 4px 0',
        }} />
      ))}
      {/* Detection pill */}
      <div style={{
        position: 'absolute', top: -16, left: '50%',
        transform: 'translateX(-50%)',
        padding: '5px 10px', borderRadius: 999,
        background: color, color: 'var(--t-bg)',
        fontFamily: 'var(--t-mono)', fontSize: 10,
        letterSpacing: '.18em', textTransform: 'uppercase',
        fontWeight: 600, whiteSpace: 'nowrap',
      }}>{label}</div>
    </div>
  );
}

// ── Detection card (bottom) ──────────────────────────────────────
function DetectionCard() {
  return (
    <div style={{
      margin: '0 20px 22px',
      padding: '14px 16px', borderRadius: 14,
      background: 'rgba(15, 20, 16, 0.75)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid var(--t-line-2)',
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: 10,
        background: 'linear-gradient(135deg, rgba(168,196,158,0.4), rgba(168,196,158,0.1))',
        display: 'grid', placeItems: 'center', flexShrink: 0,
      }}>
        <Icons.Sprout color="var(--t-sage)" size={20} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Display size={18} style={{ display: 'block', lineHeight: 1.1 }}>
          Pomidor <span style={{ fontStyle: 'italic', color: 'var(--t-sage)' }}>Malinowy Ożarowski</span>?
        </Display>
        <Meta>Pewność 94% · Solanum lycopersicum · DEMO</Meta>
      </div>
      <span style={{
        fontFamily: 'var(--t-mono)', fontSize: 10,
        letterSpacing: '.18em', textTransform: 'uppercase',
        color: 'var(--t-sage)', whiteSpace: 'nowrap',
      }}>Tak →</span>
    </div>
  );
}

// ── Dema viewfinder gdy brak permission ──────────────────────────
function DemaViewfinder({ permissionState }) {
  return (
    <div style={{
      position: 'absolute', inset: 0,
      background: 'radial-gradient(circle at 50% 45%, #1a2318 0%, #0a0e0b 60%, #000 100%)',
      display: 'grid', placeItems: 'center',
    }}>
      <svg width="260" height="320" viewBox="0 0 260 320" fill="none" style={{ opacity: 0.85 }}>
        <path d="M130 310V140" stroke="var(--t-sage)" strokeWidth="3" strokeLinecap="round" />
        <path d="M130 140C130 105 95 75 55 75c0 40 35 65 75 65zM130 140c0-28 28-55 68-55 0 30-28 55-68 55zM130 200c0-22 22-42 55-42 0 24-22 42-55 42zM130 200c0-18-18-35-45-35 0 20 18 35 45 35z"
              fill="rgba(168,196,158,0.2)" stroke="var(--t-sage)" strokeWidth="2.5" strokeLinejoin="round" />
        <circle cx="80" cy="195" r="14" fill="var(--t-rust)" opacity=".9" />
        <circle cx="165" cy="215" r="18" fill="var(--t-rust)" opacity=".9" />
        <circle cx="115" cy="240" r="12" fill="var(--t-amber)" opacity=".9" />
      </svg>

      {permissionState === 'denied' && (
        <div style={{
          position: 'absolute', bottom: '38%', left: 24, right: 24,
          padding: '14px 18px', borderRadius: 12,
          background: 'rgba(192, 106, 74, 0.18)',
          border: '1px solid var(--t-rust)',
          textAlign: 'center',
        }}>
          <Meta color="rust" style={{ marginBottom: 6 }}>Brak dostępu do kamery</Meta>
          <div style={{ fontSize: 12, color: 'var(--t-text-dim)', lineHeight: 1.5 }}>
            Zezwól w ustawieniach przeglądarki, żeby zrobić zdjęcie. To jest demo —
            poniższe kontrolki działają bez kamery.
          </div>
        </div>
      )}
    </div>
  );
}
